import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Slider } from './ui/slider';
import {
  MapPin, Search, Star, Users, ThumbsUp, Navigation,
  Filter, SortAsc, Eye, Camera, Mountain, Building, Trees
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useI18n } from '../context/I18nContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mock community spots (in production, these would come from API)
const COMMUNITY_SPOTS = [
  {
    id: 'comm-1',
    name: 'Morskie Oko Viewpoint',
    latitude: 49.2012,
    longitude: 20.0713,
    description: 'Iconic Tatra Mountains lake. Best at sunrise for reflections.',
    category: 'mountain',
    rating: 4.8,
    reviews: 124,
    shotScore: 85,
    contributor: 'MountainPhoto',
    verified: true,
    tags: ['sunrise', 'mountains', 'lake', 'iconic']
  },
  {
    id: 'comm-2',
    name: 'Warsaw Old Town Square',
    latitude: 52.2495,
    longitude: 21.0123,
    description: 'Historic architecture. Golden hour lights up the colorful facades.',
    category: 'urban',
    rating: 4.5,
    reviews: 89,
    shotScore: 72,
    contributor: 'UrbanLens',
    verified: true,
    tags: ['architecture', 'golden-hour', 'historic']
  },
  {
    id: 'comm-3',
    name: 'Bieszczady Dark Sky',
    latitude: 49.1167,
    longitude: 22.4500,
    description: 'One of the darkest skies in Poland. Perfect for Milky Way.',
    category: 'astro',
    rating: 4.9,
    reviews: 67,
    shotScore: 92,
    contributor: 'AstroHunter',
    verified: true,
    tags: ['milky-way', 'dark-sky', 'astrophotography']
  },
  {
    id: 'comm-4',
    name: 'Gdańsk Motława River',
    latitude: 54.3520,
    longitude: 18.6570,
    description: 'Classic crane view. Blue hour reflections are stunning.',
    category: 'urban',
    rating: 4.6,
    reviews: 112,
    shotScore: 78,
    contributor: 'CoastalShots',
    verified: true,
    tags: ['blue-hour', 'reflections', 'architecture']
  },
  {
    id: 'comm-5',
    name: 'Białowieża Forest',
    latitude: 52.7000,
    longitude: 23.8667,
    description: 'Ancient primeval forest. Misty mornings with wildlife.',
    category: 'nature',
    rating: 4.7,
    reviews: 45,
    shotScore: 68,
    contributor: 'WildPoland',
    verified: false,
    tags: ['forest', 'wildlife', 'mist', 'nature']
  }
];

const SpotDiscovery = ({ userLatitude, userLongitude, onSelectSpot }) => {
  const { t } = useI18n();
  const [spots, setSpots] = useState(COMMUNITY_SPOTS);
  const [searchQuery, setSearchQuery] = useState('');
  const [minScore, setMinScore] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('distance');
  const [userSpots, setUserSpots] = useState([]);

  // Load user's spots
  useEffect(() => {
    const fetchUserSpots = async () => {
      try {
        const response = await axios.get(`${API_URL}/spots`);
        setUserSpots(response.data);
      } catch (error) {
        // User might not be logged in
      }
    };
    fetchUserSpots();
  }, []);

  // Calculate distance between two coordinates
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Filter and sort spots
  const filteredSpots = useMemo(() => {
    let result = [...spots];

    // Add distance to each spot
    result = result.map(spot => ({
      ...spot,
      distance: calculateDistance(userLatitude, userLongitude, spot.latitude, spot.longitude)
    }));

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(spot => 
        spot.name.toLowerCase().includes(query) ||
        spot.description.toLowerCase().includes(query) ||
        spot.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      result = result.filter(spot => spot.category === categoryFilter);
    }

    // Score filter
    result = result.filter(spot => spot.shotScore >= minScore);

    // Sort
    switch (sortBy) {
      case 'distance':
        result.sort((a, b) => a.distance - b.distance);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'score':
        result.sort((a, b) => b.shotScore - a.shotScore);
        break;
      default:
        break;
    }

    return result;
  }, [spots, searchQuery, categoryFilter, minScore, sortBy, userLatitude, userLongitude]);

  const categories = [
    { value: 'all', label: t('discover.all'), icon: MapPin },
    { value: 'mountain', label: t('discover.mountains'), icon: Mountain },
    { value: 'urban', label: t('discover.urban'), icon: Building },
    { value: 'nature', label: t('discover.nature'), icon: Trees },
    { value: 'astro', label: t('discover.astro'), icon: Star },
  ];

  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.icon : MapPin;
  };

  const handleSelectSpot = (spot) => {
    if (onSelectSpot) {
      onSelectSpot({ lat: spot.latitude, lng: spot.longitude });
      toast.success(`${t('discover.selected')}: ${spot.name}`);
    }
  };

  const handleSaveSpot = async (spot) => {
    try {
      await axios.post(`${API_URL}/spots`, {
        name: spot.name,
        latitude: spot.latitude,
        longitude: spot.longitude,
        description: spot.description,
        tags: spot.tags
      });
      toast.success(t('discover.spotSaved'));
    } catch (error) {
      toast.error(t('discover.spotSaveFailed'));
    }
  };

  return (
    <div className="space-y-4">
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-400" />
            {t('discover.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('discover.search')}
              className="pl-10 bg-slate-800 border-white/10"
              data-testid="spot-search-input"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <Button
                key={cat.value}
                variant={categoryFilter === cat.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCategoryFilter(cat.value)}
                className={categoryFilter === cat.value 
                  ? 'bg-purple-500 text-white' 
                  : 'border-white/10 text-slate-400'
                }
                data-testid={`filter-${cat.value}`}
              >
                <cat.icon className="w-3 h-3 mr-1" />
                {cat.label}
              </Button>
            ))}
          </div>

          {/* Score Filter */}
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="text-xs text-slate-400">{t('discover.minScore')}: {minScore}%</span>
            <Slider
              value={[minScore]}
              onValueChange={([v]) => setMinScore(v)}
              min={0}
              max={90}
              step={10}
              className="flex-1"
            />
          </div>

          {/* Sort */}
          <div className="flex gap-2">
            <span className="text-xs text-slate-500 flex items-center">
              <SortAsc className="w-3 h-3 mr-1" />
              {t('discover.sort')}:
            </span>
            {[
              { value: 'distance', label: t('discover.nearest') },
              { value: 'rating', label: t('discover.rating') },
              { value: 'score', label: t('discover.score') }
            ].map(sort => (
              <Button
                key={sort.value}
                variant="ghost"
                size="sm"
                onClick={() => setSortBy(sort.value)}
                className={sortBy === sort.value 
                  ? 'text-amber-500 bg-amber-500/10' 
                  : 'text-slate-400'
                }
              >
                {sort.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">
              {filteredSpots.length} {t('discover.spotsFound')}
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[400px]">
            <div className="p-4 space-y-3">
              {filteredSpots.map(spot => {
                const CategoryIcon = getCategoryIcon(spot.category);
                return (
                  <div
                    key={spot.id}
                    className="p-4 rounded-lg bg-slate-800/50 border border-white/10 hover:border-purple-500/30 transition-all cursor-pointer"
                    onClick={() => handleSelectSpot(spot)}
                    data-testid={`community-spot-${spot.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                          <CategoryIcon className="w-4 h-4 text-purple-400" />
                        </div>
                        <div>
                          <h4 className="text-white font-medium flex items-center gap-1">
                            {spot.name}
                            {spot.verified && (
                              <Badge variant="secondary" className="ml-1 text-xs bg-emerald-500/20 text-emerald-400">
                                ✓
                              </Badge>
                            )}
                          </h4>
                          <p className="text-xs text-slate-500">
                            {t('discover.by')} {spot.contributor} • {spot.distance.toFixed(1)}km {t('discover.away')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-amber-500">
                          <Star className="w-4 h-4 fill-current" />
                          <span className="font-mono">{spot.rating}</span>
                        </div>
                        <p className="text-xs text-slate-500">{spot.reviews} {t('discover.reviews')}</p>
                      </div>
                    </div>

                    <p className="text-sm text-slate-400 mb-3">{spot.description}</p>

                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap gap-1">
                        {spot.tags.slice(0, 3).map(tag => (
                          <Badge key={tag} variant="outline" className="text-xs border-white/10 text-slate-400">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`px-2 py-1 rounded text-xs font-mono ${
                          spot.shotScore >= 80 ? 'bg-emerald-500/20 text-emerald-400' :
                          spot.shotScore >= 60 ? 'bg-amber-500/20 text-amber-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          {spot.shotScore}%
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveSpot(spot);
                          }}
                          className="text-slate-400 hover:text-amber-500"
                          data-testid={`save-community-spot-${spot.id}`}
                        >
                          <MapPin className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {filteredSpots.length === 0 && (
                <div className="text-center py-8">
                  <Search className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-400">{t('discover.noSpotsFound')}</p>
                  <p className="text-xs text-slate-500">{t('discover.tryAdjustingFilters')}</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Contribute CTA */}
      <Card className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">{t('discover.shareYourSpots')}</h4>
              <p className="text-sm text-slate-300">{t('discover.helpCommunity')}</p>
            </div>
            <Button className="bg-purple-500 hover:bg-purple-400 text-white" data-testid="contribute-spot-btn">
              <Camera className="w-4 h-4 mr-2" />
              {t('discover.contribute')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SpotDiscovery;
